import { useState, useEffect, useRef } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import { MessageCircle } from 'lucide-react';
import { ConversationList } from '../components/chat/ConversationList';
import { ChatView } from '../components/chat/ChatView';
import { useSocket } from '../hooks/useSocket';
import { useStartConversationMutation } from '@nimbazaar/store';

export function InboxPage() {
  const { conversationId } = useParams<{ conversationId?: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [selectedId, setSelectedId] = useState<string | undefined>(conversationId);
  const [startConversation] = useStartConversationMutation();
  const { socket, isConnected, onlineUsers, typingUsers } = useSocket();
  const startedRef = useRef(false);

  // Handle /inbox?ad=X&seller=Y — auto-start a conversation
  useEffect(() => {
    const adId = searchParams.get('ad');
    const sellerId = searchParams.get('seller');
    const message = searchParams.get('message');

    if (adId && sellerId && message && !startedRef.current) {
      startedRef.current = true;
      startConversation({ adId, sellerId, message })
        .unwrap()
        .then((res) => {
          setSelectedId(res.data.id);
          navigate(`/inbox/${res.data.id}`, { replace: true });
        })
        .catch(() => {
          // Conversation may already exist - handled by backend
        });
    }
  }, [searchParams, startConversation, navigate]);

  // Sync with URL param
  useEffect(() => {
    if (conversationId) {
      setSelectedId(conversationId);
    }
  }, [conversationId]);

  // Mobile: show list or chat
  const showChat = !!selectedId;

  return (
    <div className="h-dvh flex bg-gray-50 overflow-hidden">
      {/* Conversation List — Left Panel */}
      <div
        className={`w-full md:w-[360px] md:min-w-[360px] border-r border-gray-200 bg-white flex flex-col ${
          showChat ? 'hidden md:flex' : 'flex'
        }`}
      >
        <ConversationList
          selectedId={selectedId}
          onSelect={(id) => {
            setSelectedId(id);
            navigate(`/inbox/${id}`, { replace: true });
          }}
          onlineUsers={onlineUsers}
        />
      </div>

      {/* Chat View — Right Panel */}
      <div className={`flex-1 flex flex-col ${showChat ? 'flex' : 'hidden md:flex'}`}>
        {selectedId ? (
          <ChatView
            conversationId={selectedId}
            socket={socket}
            isConnected={isConnected}
            onlineUsers={onlineUsers}
            typingUsers={typingUsers}
            onBack={() => {
              setSelectedId(undefined);
              navigate('/inbox', { replace: true });
            }}
          />
        ) : (
          <div className="flex-1 flex items-center justify-center">
            <div className="text-center">
              <div className="w-20 h-20 bg-primary-50 rounded-full flex items-center justify-center mx-auto mb-4">
                <MessageCircle className="w-10 h-10 text-primary-300" />
              </div>
              <h3 className="text-lg font-semibold text-gray-700 mb-1">Your Messages</h3>
              <p className="text-sm text-gray-400">
                Select a conversation to start chatting
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
